'use client'

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from './SearchableSelect'
import type { Catalog } from './entryTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { SibsEntry } from '../EntryDetail/SibsWidgets'

export type BulkClassifyForm = {
  revisionStatus: 'accept' | 'accept-hidden'
  isScam: boolean
  messageTypeId: number | null
  platformId: number | null
}

export type BulkClassifyResult = {
  successCount: number
  failures: Array<{ rawEntryId: string; error?: unknown }>
}

// Patches a freshly-classified batch into an already-loaded entries list in
// place, instead of the caller re-fetching page 0 and discarding whatever
// the user had scrolled/loaded beyond it. That refetch-based approach was
// the original one here, and it's exactly why bulk-accepted rows used to
// vanish from the table right after a successful classify: fetchEntries(0,
// true)/fetchAlertedEmails(0) both replace the whole list with just the
// first page (see their own "replace, don't append" comments), so anything
// paged in via infinite scroll — including the rows just acted on — dropped
// out of view even though nothing was wrong server-side. Matches exactly the
// four fields bulkClassifyRawEntries is allowed to set; platform_namelong is
// re-resolved from the catalog since that's a display-only join the raw id
// alone can't give us.
export function applyBulkClassifyPatch(
  entries: SibsEntry[],
  succeededRawEntryIds: Set<string>,
  form: BulkClassifyForm,
  catalog: Catalog,
): SibsEntry[] {
  const platform = catalog.platforms.find((p) => p.id === form.platformId)
  const messageType = catalog.messageTypes.find((m) => m.id === form.messageTypeId)
  return entries.map((entry) => {
    const rawEntryId = entry.raw_entry_id ?? entry.id
    if (!succeededRawEntryIds.has(rawEntryId)) return entry
    return {
      ...entry,
      revision_status: form.revisionStatus,
      is_scam: form.isScam,
      platform_id: form.platformId,
      platform_namelong: platform ? (platform.name_long ?? platform.name) : entry.platform_namelong,
      message_type_id: form.messageTypeId,
      message_type_text: messageType ? (messageType.name_long ?? messageType.name) : entry.message_type_text,
      // branch_multibanco's source_type is dynamic (raw_entry until a
      // processed_entries row exists — see get_sibs_fraud_entries) — a plain
      // accept never creates a brand_campaign, so 'processed_entry' is
      // always the right post-accept label here; branch_processed rows were
      // already 'processed_entry' and stay that way.
      source_type: entry.source_type === 'raw_entry' ? 'processed_entry' : entry.source_type,
    }
  })
}

// The multi-select "raw_entry classification step" — one dialog, table-
// agnostic (only needs a selection count + catalog + submit handler), so any
// table listing raw/unified entries can drop this in behind its own
// checkbox column rather than each building its own version. First wired up
// from CampaignsView's raw entries table (SibsMailView.tsx), which already
// had the checkbox/toggleAll infrastructure this needs.
//
// Deliberately a small fixed field set (status, is_scam, message type,
// platform) — not a general bulk-edit form — since that's genuinely
// everything that can be defined uniformly across a batch of otherwise-
// unrelated raw entries; sender/message content/call type stay whatever
// each entry's own raw_entries row already has (see bulkClassifyRawEntries
// on the backend).
export function BulkClassifyDialog({
  open,
  onOpenChange,
  count,
  catalog,
  theme,
  submitting,
  result,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  count: number
  catalog: Catalog
  /** Styles the message type/platform pickers — same SearchableSelect single-entry classification (AssignFieldsWidget) uses, so the two look and behave identically. */
  theme: ProcessedTheme
  submitting: boolean
  result: BulkClassifyResult | null
  onSubmit: (form: BulkClassifyForm) => void
}) {
  const [revisionStatus, setRevisionStatus] = useState<'accept' | 'accept-hidden'>('accept')
  const [isScam, setIsScam] = useState(true)
  const [messageTypeId, setMessageTypeId] = useState<number | null>(null)
  const [platformId, setPlatformId] = useState<number | null>(null)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Classify {count} {count === 1 ? 'entry' : 'entries'}</DialogTitle>
          <DialogDescription>
            Applies the same status, scam flag, message type and platform to every selected entry. Sender, message content and call type are kept as-is from each entry&apos;s own raw data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
            <div className="mt-1.5 flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={revisionStatus === 'accept' ? 'default' : 'outline'}
                onClick={() => setRevisionStatus('accept')}
              >
                Accept
              </Button>
              <Button
                type="button"
                size="sm"
                variant={revisionStatus === 'accept-hidden' ? 'default' : 'outline'}
                onClick={() => setRevisionStatus('accept-hidden')}
              >
                Accept Hidden
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Is scam?</Label>
            <div className="mt-1.5 flex gap-2">
              <Button type="button" size="sm" variant={isScam ? 'default' : 'outline'} onClick={() => setIsScam(true)}>
                Yes
              </Button>
              <Button type="button" size="sm" variant={!isScam ? 'default' : 'outline'} onClick={() => setIsScam(false)}>
                No
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Message Type</Label>
            <div className="mt-1.5">
              <SearchableSelect
                theme={theme}
                value={messageTypeId}
                options={catalog.messageTypes}
                placeholder="Select type…"
                searchPlaceholder="Search types…"
                onChange={(id) => setMessageTypeId(id != null ? Number(id) : null)}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Platform</Label>
            <div className="mt-1.5">
              <SearchableSelect
                theme={theme}
                value={platformId}
                options={catalog.platforms}
                placeholder="Select platform…"
                searchPlaceholder="Search platforms…"
                noneLabel="No platform"
                onChange={(id) => setPlatformId(id != null && id !== '' ? Number(id) : null)}
              />
            </div>
          </div>

          {result && (
            <p className={`text-[12px] ${result.failures.length === 0 ? 'text-success' : 'text-warning'}`}>
              {result.failures.length === 0
                ? `All ${result.successCount} entries classified successfully.`
                : `${result.successCount} succeeded, ${result.failures.length} failed.`}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={submitting || count === 0}
            onClick={() => onSubmit({ revisionStatus, isScam, messageTypeId, platformId })}
          >
            {submitting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Classify {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
